'use client';

import { useState, useRef, KeyboardEvent } from "react";

interface ChatInputProps {
  onSend?: (message: string) => void;
  placeholder?: string;
  tags?: { label: string; onRemove: () => void }[];
  isSending?: boolean;
}

export default function ChatInput({ 
  onSend, 
  placeholder = "请输入提示词", 
  tags = [],
  isSending = false 
}: ChatInputProps) {
  const [message, setMessage] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = () => {
    if (message.trim() && !isSending) {
      onSend?.(message.trim());
      setMessage("");
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClear = () => {
    setMessage("");
    textareaRef.current?.focus();
  };

  return (
    <div 
      className="w-full max-w-180 min-h-[140px] rounded-2xl p-5 
                 bg-border backdrop-blur-[25px]
                 border border-white/10
                 shadow-[inset_0_0_7px_0_rgba(255,255,255,0.35)]
                 focus-within:border-primary-purple/30
                 focus-within:shadow-[inset_0_0_7px_0_rgba(255,255,255,0.35),0_0_20px_rgba(101,45,245,0.15)]
                 transition-all duration-300 relative group"
    >
      {/* Tags */}
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {tags.map((tag, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1.5 h-7 pl-3 pr-2 text-xs font-medium
                         bg-linear-to-r from-primary-purple/20 to-primary-pink/20 
                         rounded-lg border border-primary-purple/20 text-white/90"
            >
              {tag.label}
              <button
                onClick={tag.onRemove}
                className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-white/10 text-white/60 hover:text-white transition-colors"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Textarea */}
      <textarea
        ref={textareaRef}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="w-full bg-transparent text-white text-[15px] leading-[1.6] placeholder:text-white/30 
                   resize-none outline-hidden min-h-[52px] mb-10"
        rows={2}
      />

      {/* Toolbar - Absolute Positioned at Bottom */}
      <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
        <div className="flex items-center gap-1">
          {/* @ Button */}
          <button 
            className="w-8 h-8 flex items-center justify-center text-white/50 hover:text-white 
                       hover:bg-white/10 rounded-lg transition-all duration-200"
            title="@提及"
          >
            <span className="text-base font-medium">@</span>
          </button>
          {/* Link Button */}
          <button 
            className="w-8 h-8 flex items-center justify-center text-white/50 hover:text-white 
                       hover:bg-white/10 rounded-lg transition-all duration-200"
            title="添加链接"
          >
            <svg className="w-4.5 h-4.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          {/* Clear Button */}
          <button 
            onClick={handleClear}
            className="w-8 h-8 flex items-center justify-center text-white/50 hover:text-white 
                       hover:bg-white/10 rounded-lg transition-all duration-200"
            title="清除内容"
          >
            <svg className="w-4.5 h-4.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M15 9l-6 6M9 9l6 6" strokeLinecap="round"/>
            </svg>
          </button>
          
          {/* Divider */}
          <div className="w-px h-4 bg-white/10 mx-2" />
          
          {/* Word Count Dropdown */}
          <button 
            className="h-8 px-3 flex items-center gap-1.5 text-xs text-white/50 hover:text-white 
                       hover:bg-white/10 rounded-lg transition-all duration-200"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" strokeLinecap="round" strokeLinejoin="round"/>
              <polyline points="14 2 14 8 20 8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>字数设置</span>
            <svg className="w-3 h-3 opacity-60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {/* Send Button */}
        <button
          onClick={handleSend}
          disabled={!message.trim() || isSending}
          className="w-10 h-10 flex items-center justify-center bg-linear-to-r from-primary-purple to-primary-pink rounded-full
                     shadow-lg shadow-primary-purple/20
                     hover:shadow-glow-purple-strong hover:scale-105 transition-all duration-200
                     disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none disabled:hover:scale-100
                     active:scale-95 group-focus-within:shadow-glow-purple"
        >
          {isSending ? (
            <svg className="w-5 h-5 text-white animate-spin" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeOpacity="0.3" />
              <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          ) : (
            <svg className="w-5 h-5 text-white translate-x-px -translate-y-px" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M12 19V5M5 12l7-7 7 7" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}
